import React, { useEffect, useRef, useCallback, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useUnit } from "effector-react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { ProgressSpinner } from "primereact/progressspinner";
import { useChatViewModel } from "../viewmodel/useChatViewModel";
import { MessageBubble } from "../components/MessageBubble";
import { ChatInput } from "../components/ChatInput";
import { BlockUserModal } from "../components/BlockUserModal";

import { $chatStore } from "../store/chatStore";
import styles from "./ChatPage.module.scss";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { SEO } from "@/core/components/SEO/SEO";

export const ChatPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const menuRef = useRef<Menu>(null);
  const [blockModalVisible, setBlockModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const chatStore = useUnit($chatStore);

  const {
    messages,
    loading,
    loadingMore,
    hasMoreMessages,
    sendingMessage,
    deletingConversation,
    blockStatus,
    blockingUser,
    blockUser,
    error,
    currentUser,
    sendMessage,
    refreshChat,
    loadMoreMessages,
    deleteConversation,
  } = useChatViewModel(conversationId);

  const otherUser = conversationId
    ? chatStore.chatUsers[conversationId]
    : undefined;
  const isLocked =
    blockStatus === "has_blocked" || blockStatus === "is_blocked";

  const menuItems = [
    ...(blockStatus === "has_blocked"
      ? [
          {
            label: "Unblock user",
            icon: "pi pi-lock-open",
            command: () => blockUser({ mode: "unblock" }),
          },
        ]
      : blockStatus === "not_blocked"
        ? [
            {
              label: `Block ${otherUser?.name ?? "user"}`,
              icon: "pi pi-ban",
              command: () => setBlockModalVisible(true),
              className: "chat-delete-item",
            },
          ]
        : []),
    {
      label: "Delete conversation",
      icon: "pi pi-trash",
      command: () => setDeleteModalVisible(true),
      className: "chat-delete-item",
    },
  ];

  const lastMessageId =
    messages.length > 0 ? messages[messages.length - 1]?.id : null;
  useEffect(() => {
    if (messagesEndRef.current && lastMessageId != null && !loadingMore) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [lastMessageId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !loadingMore &&
      prevScrollHeightRef.current > 0 &&
      messagesContainerRef.current
    ) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      messagesContainerRef.current.scrollTop =
        newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [messages.length, loadingMore]);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (
      container.scrollTop === 0 &&
      hasMoreMessages &&
      !loadingMore &&
      !loading
    ) {
      prevScrollHeightRef.current = container.scrollHeight;
      loadMoreMessages();
    }
  }, [hasMoreMessages, loadingMore, loading, loadMoreMessages]);

  const handleDeleteConfirm = () => {
    deleteConversation();
    setDeleteModalVisible(false);
  };

  const handleBack = () => {
    navigate("/messages");
  };

  if (!conversationId) {
    return (
      <div className={styles.errorContainer}>
        <p>No conversation selected</p>
      </div>
    );
  }

  return (
    <>
      <SEO title={`Messages | EUcalls`} description={"Messages"} />

      <div className={styles.chatPage}>
        <div className={styles.chatHeader}>
          <div className={styles.headerLeft}>
            <button
              className={styles.backBtn}
              onClick={handleBack}
              aria-label="Back to conversations"
            >
              <i className="pi pi-arrow-left" />
            </button>
            <Link
              to={otherUser ? `/users/${otherUser.id}` : "#"}
              className={styles.headerUserLink}
            >
              <div className={styles.avatar}>
                {otherUser?.avatar ? (
                  <img src={otherUser.avatar} alt={otherUser.name || "User"} />
                ) : (
                  <div className={styles.avatarPlaceholder}>
                    <i className="pi pi-user" />
                  </div>
                )}
              </div>
              <div className={styles.headerInfo}>
                <h2>{otherUser?.name || `Conversation ${conversationId}`}</h2>
                {otherUser?.job && (
                  <span className={styles.headerJob}>{otherUser.job}</span>
                )}
              </div>
            </Link>
          </div>
          <div className={styles.headerActions}>
            <Button
              icon="pi pi-ellipsis-v"
              className="p-button-text"
              onClick={(e) => menuRef.current?.toggle(e)}
              disabled={deletingConversation}
            />
            <Menu ref={menuRef} model={menuItems} popup />
          </div>
        </div>

        <div
          className={styles.messagesContainer}
          ref={messagesContainerRef}
          onScroll={handleScroll}
        >
          {loading && messages.length === 0 ? (
            <div className={styles.loadingContainer}>
              <ProgressSpinner />
              <p>Loading messages...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <i className="pi pi-exclamation-circle" />
              <p>{error}</p>
              <Button
                label="Retry"
                icon="pi pi-refresh"
                onClick={refreshChat}
                className="p-button-sm"
              />
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyContainer}>
              <i className="pi pi-comments" />
              <p>No messages yet</p>
              <small>Start the conversation by sending a message below</small>
            </div>
          ) : (
            <div className={styles.messagesList}>
              {loadingMore && (
                <div className={styles.loadingMoreContainer}>
                  <ProgressSpinner
                    style={{ width: "1.5rem", height: "1.5rem" }}
                  />
                </div>
              )}
              {messages
                .slice()
                .reverse()
                .map((message) => {
                  const currentUid =
                    typeof currentUser?.uid === "string"
                      ? parseInt(currentUser.uid, 10)
                      : currentUser?.uid;
                  const isOwnMessage = message.source_uid === currentUid;
                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      isOwnMessage={isOwnMessage}
                      showAvatar={!isOwnMessage}
                      avatarUrl={otherUser?.avatar}
                    />
                  );
                })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {blockStatus === "has_blocked" && (
          <div className={styles.blockedBanner}>
            <i className="pi pi-ban" />
            <span>
              You have blocked this user.{" "}
              <button
                className={styles.bannerAction}
                onClick={() => blockUser({ mode: "unblock" })}
                disabled={blockingUser}
              >
                Unblock
              </button>
            </span>
          </div>
        )}
        {blockStatus === "is_blocked" && (
          <div className={styles.blockedBanner}>
            <i className="pi pi-lock" />
            <span>You have been blocked by this user.</span>
          </div>
        )}

        <ChatInput
          onSend={sendMessage}
          disabled={!!error || isLocked}
          sending={sendingMessage}
        />

        <BlockUserModal
          visible={blockModalVisible}
          otherUserName={otherUser?.name ?? "this user"}
          blocking={blockingUser}
          onConfirm={(reasonId, reasonText) => {
            blockUser({
              mode: "block",
              block_reason_id: reasonId,
              block_reason_text: reasonText,
            });
            setBlockModalVisible(false);
          }}
          onHide={() => setBlockModalVisible(false)}
        />

        <CommonModal
          isOpen={deleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          icon="icon-delete"
          hasForm={false}
          width={430}
          height={480}
        >
          <div className="confirmation-modal">
            <h3>Delete conversation for both users?</h3>
            <p>
              This conversation will be permanently deleted for you and the
              other user. This action cannot be undone.
            </p>
            <div className="form-actions">
              <Button
                label="Cancel"
                autoFocus={false}
                className="btn-secondary"
                onClick={() => setDeleteModalVisible(false)}
                disabled={deletingConversation}
              />
              <Button
                label="Delete"
                className="btn-danger"
                severity="danger"
                onClick={handleDeleteConfirm}
                loading={deletingConversation}
              />
            </div>
          </div>
        </CommonModal>
      </div>
    </>
  );
};
